import { useState, useCallback } from "react";
import { Button } from "../../ui/button";
import { Label } from "../../ui/label";
import { toast } from "sonner";
import { Loader2, Shield, Info } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Line, ComposedChart,
} from "recharts";
import { CollapsibleSection } from "./CollapsibleSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ParetoResultsView({ result, datasetName }) {
  const [metadataOpen, setMetadataOpen] = useState(false);
  const conc = result.concentration_analysis || {};
  const level = conc.concentration_level || "distributed_contribution";
  const catTo80 = conc.categories_to_80 || result.vital_few?.length || 0;
  const totalCats = conc.total_categories || result.data?.length || 0;
  const pctTo80 = conc.percent_categories_to_80 != null ? conc.percent_categories_to_80 : (totalCats > 0 ? catTo80 / totalCats : 0);
  const isDistributed = level === "distributed_contribution";
  const isStrong = level === "strong_concentration";
  const isModerate = level === "moderate_concentration";

  let bannerBg, bannerBorder, bannerText, bannerDot, bannerLabel;
  if (isStrong) { bannerBg = "bg-emerald-500/10"; bannerBorder = "border-emerald-500/30"; bannerText = "text-emerald-400"; bannerDot = "bg-emerald-400"; bannerLabel = "Strong concentration detected \u2014 Few categories dominate total contribution"; }
  else if (isModerate) { bannerBg = "bg-amber-500/10"; bannerBorder = "border-amber-500/30"; bannerText = "text-amber-400"; bannerDot = "bg-amber-400"; bannerLabel = "Moderate concentration detected"; }
  else { bannerBg = "bg-blue-500/10"; bannerBorder = "border-blue-500/30"; bannerText = "text-blue-400"; bannerDot = "bg-blue-400"; bannerLabel = "No strong Pareto concentration detected \u2014 Contribution is broadly distributed"; }

  const topLabel = isDistributed ? "Top Contributors (No Dominant Few)" : "Vital Few";

  return (
    <div className="space-y-5" data-testid="pareto-results-view">
      <div className={`p-4 ${bannerBg} border ${bannerBorder} rounded-lg`} data-testid="pareto-concentration-banner">
        <div className="flex items-center gap-2.5 mb-1.5">
          <span className={`w-2.5 h-2.5 rounded-full ${bannerDot}`}></span>
          <span className={`text-sm font-semibold ${bannerText}`} data-testid="pareto-concentration-label">{bannerLabel}</span>
          <span className={`ml-auto px-2 py-0.5 text-[10px] font-medium rounded-full ${bannerBg} ${bannerText} border ${bannerBorder}`} data-testid="pareto-concentration-badge">{isStrong ? "Strong" : isModerate ? "Moderate" : "Distributed"}</span>
        </div>
        <p className="text-sm text-muted-foreground" data-testid="pareto-concentration-detail">{catTo80} of {totalCats} categories (~{Math.round(pctTo80 * 100)}%) required to reach 80% of total contribution</p>
        {conc.hhi_score != null && conc.hhi_score > 0 && <p className="text-xs text-muted-foreground mt-1">HHI Score: {conc.hhi_score.toFixed(4)}</p>}
      </div>

      <div className="flex flex-wrap gap-2 items-center" data-testid="pareto-top-contributors">
        <span className="text-sm text-muted-foreground font-medium">{topLabel}:</span>
        {(result.vital_few || []).map((item) => <span key={item} className="px-2.5 py-1 bg-primary/10 text-primary text-sm rounded-md font-medium">{item}</span>)}
      </div>

      <div className="h-[400px]" data-testid="pareto-chart">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={result.data} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#2A2F3A" />
            <XAxis dataKey="category" stroke="#6B7280" tick={{ fill: "#A8B0BF", fontSize: 11 }} angle={-45} textAnchor="end" height={80} />
            <YAxis yAxisId="left" stroke="#6B7280" tick={{ fill: "#A8B0BF", fontSize: 12 }} />
            <YAxis yAxisId="right" orientation="right" stroke="#6B7280" tick={{ fill: "#A8B0BF", fontSize: 12 }} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
            <Tooltip contentStyle={{ backgroundColor: "#1A1D23", border: "1px solid #2A2F3A", borderRadius: "8px" }} labelStyle={{ color: "#F3F5F8" }} />
            <Bar yAxisId="left" dataKey="value" fill="#2E66FF" radius={[4, 4, 0, 0]} />
            <Line yAxisId="right" type="monotone" dataKey="cumulative_percentage" stroke="#E6A23C" strokeWidth={2} dot={{ fill: "#E6A23C", r: 4 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="p-3 bg-muted/30 border border-border rounded-lg" data-testid="pareto-governance-banner">
        <div className="flex items-start gap-2"><Shield className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" /><p className="text-xs text-muted-foreground">Pareto analysis is descriptive. It identifies contribution concentration \u2014 not statistical significance, causation, or validated root cause.</p></div>
      </div>

      <div className="overflow-x-auto" data-testid="pareto-data-table">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border"><th className="text-left py-2 px-4 font-medium text-muted-foreground">Category</th><th className="text-right py-2 px-4 font-medium text-muted-foreground">Value</th><th className="text-right py-2 px-4 font-medium text-muted-foreground">%</th><th className="text-right py-2 px-4 font-medium text-muted-foreground">Cumulative %</th></tr></thead>
          <tbody>{result.data.map((item, i) => (
            <tr key={i} className="border-b border-border/50"><td className="py-2 px-4 font-medium">{item.category}</td><td className="py-2 px-4 text-right font-mono">{item.value.toLocaleString()}</td><td className="py-2 px-4 text-right">{item.percentage}%</td><td className="py-2 px-4 text-right">{item.cumulative_percentage}%</td></tr>
          ))}</tbody>
        </table>
      </div>

      <CollapsibleSection title="Analysis Metadata" icon={<Info className="w-4 h-4 text-primary" />} open={metadataOpen} onToggle={() => setMetadataOpen(v => !v)} testId="pareto-analysis-metadata">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm" data-testid="pareto-metadata-grid">
          <div><span className="text-muted-foreground">Aggregation:</span> <span className="font-medium ml-1">{result.aggregation_method === "sum" ? "SUM(Value Column)" : "COUNT(Category)"}</span></div>
          <div><span className="text-muted-foreground">Category Count:</span> <span className="font-medium ml-1">{totalCats}</span></div>
          <div><span className="text-muted-foreground">Total Value:</span> <span className="font-medium ml-1">{(result.total_value || 0).toLocaleString()}</span></div>
          <div><span className="text-muted-foreground">Dataset:</span> <span className="font-medium ml-1">{datasetName || "N/A"}</span></div>
          <div><span className="text-muted-foreground">Timestamp:</span> <span className="font-mono text-muted-foreground ml-1">{new Date().toISOString().split("T")[0]}</span></div>
          <div><span className="text-muted-foreground">Classification:</span> <span className="font-medium ml-1">{level.replace(/_/g, " ")}</span></div>
        </div>
      </CollapsibleSection>
    </div>
  );
}

export default function ParetoTab({ projectId, token, datasets, selectedDataset, setSelectedDataset, getCategoricalColumns, getNumericColumns }) {
  const [paretoColumn, setParetoColumn] = useState("");
  const [valueColumn, setValueColumn] = useState("");
  const [paretoResult, setParetoResult] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);

  const generatePareto = useCallback(async () => {
    if (!selectedDataset || !paretoColumn) { toast.error("Please select a dataset and category column"); return; }
    setAnalyzing(true);
    try {
      let url = `${API}/projects/${projectId}/datasets/${selectedDataset.dataset_id}/pareto?category_column=${paretoColumn}`;
      if (valueColumn) url += `&value_column=${valueColumn}`;
      const response = await fetch(url, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) { const error = await response.json(); throw new Error(error.detail || "Analysis failed"); }
      const data = await response.json();
      setParetoResult(data);
      toast.success("Pareto analysis complete");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setAnalyzing(false);
    }
  }, [projectId, token, selectedDataset, paretoColumn, valueColumn]);

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <h3 className="font-heading font-semibold mb-4">Pareto Analysis</h3>
      <div className="grid md:grid-cols-4 gap-4 mb-6">
        <div className="space-y-2">
          <Label>Dataset</Label>
          <select value={selectedDataset?.dataset_id || ""} onChange={(e) => { const ds = datasets.find((d) => d.dataset_id === e.target.value); setSelectedDataset(ds); setParetoColumn(""); setValueColumn(""); setParetoResult(null); }} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" data-testid="pareto-dataset-select">
            <option value="">Select dataset</option>
            {datasets.map((ds) => <option key={ds.dataset_id} value={ds.dataset_id}>{ds.filename}</option>)}
          </select>
        </div>
        <div className="space-y-2">
          <Label>Category Column</Label>
          <select value={paretoColumn} onChange={(e) => setParetoColumn(e.target.value)} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" disabled={!selectedDataset} data-testid="pareto-category-select">
            <option value="">Select category</option>
            {getCategoricalColumns().map((col) => <option key={col} value={col}>{col}</option>)}
          </select>
        </div>
        <div className="space-y-2">
          <Label>Value Column (Optional)</Label>
          <select value={valueColumn} onChange={(e) => setValueColumn(e.target.value)} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" disabled={!selectedDataset} data-testid="pareto-value-select">
            <option value="">Count occurrences</option>
            {getNumericColumns().map((col) => <option key={col} value={col}>{col}</option>)}
          </select>
        </div>
        <div className="flex items-end">
          <Button onClick={generatePareto} disabled={!selectedDataset || !paretoColumn || analyzing} className="w-full" data-testid="generate-pareto-btn">
            {analyzing ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Analyzing...</>) : "Generate Pareto"}
          </Button>
        </div>
      </div>
      {paretoResult && <ParetoResultsView result={paretoResult} datasetName={selectedDataset?.filename} />}
    </div>
  );
}
